import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  FileText,
  Bell,
  TrendingUp,
  AlertCircle,
  Loader,
  ArrowLeft,
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { getAllPrograms, getUserNotifications } from '../services/programService';
import ProgramUploadModal from '../components/ProgramUploadModal';
import AlertModal from '../components/AlertModal';

const AdminProgramsDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, userRole, loading: authLoading } = useContext(AuthContext);
  const [programs, setPrograms] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    totalPrograms: 0,
    totalReactions: 0,
    avgReactions: 0,
    topProgram: null,
  });
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);

  const isAdmin = ['master_admin', 'overseer', 'colony_prime_minister'].includes(userRole);

  useEffect(() => {
    if (isAdmin && currentUser) {
      fetchDashboardData();
    }
  }, [isAdmin, currentUser]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const programsData = await getAllPrograms();
      setPrograms(programsData);

      // Calculate stats
      const totalPrograms = programsData.length;
      const totalReactions = programsData.reduce(
        (sum, p) => sum + (p.reactionsCount || 0),
        0
      );
      const avgReactions = totalPrograms > 0 ? (totalReactions / totalPrograms).toFixed(1) : 0;
      const topProgram =
        programsData.length > 0
          ? programsData.reduce((max, p) => 
              (p.reactionsCount || 0) > (max.reactionsCount || 0) ? p : max
            )
          : null;

      setStats({
        totalPrograms,
        totalReactions,
        avgReactions,
        topProgram,
      });

      // Fetch notifications
      const notifData = await getUserNotifications(currentUser.uid);
      setNotifications(notifData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin mx-auto text-blue-600 mb-4" size={48} />
          <p className="text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-600 mb-4" size={48} />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only admins can access this dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-900 shadow-lg border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Programs Dashboard</h1>
              <p className="text-slate-400 mt-1">Manage programs and send alerts</p>
            </div>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white transition"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                + Upload Program
              </button>
              <button
                onClick={() => setShowAlertModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                📢 Send Alert
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader className="animate-spin text-blue-400" size={40} />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {/* Total Programs */}
              <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-blue-500 transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Total Programs</p>
                    <p className="text-3xl font-bold text-white">{stats.totalPrograms}</p>
                  </div>
                  <FileText className="text-blue-400" size={32} />
                </div>
              </div>

              {/* Total Reactions */}
              <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-purple-500 transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Total Reactions</p>
                    <p className="text-3xl font-bold text-white">{stats.totalReactions}</p>
                  </div>
                  <TrendingUp className="text-purple-400" size={32} />
                </div>
              </div>

              {/* Average Reactions */}
              <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-yellow-500 transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Avg Reactions/Program</p>
                    <p className="text-3xl font-bold text-white">{stats.avgReactions}</p>
                  </div>
                  <BarChart3 className="text-yellow-400" size={32} />
                </div>
              </div>

              {/* Unread Notifications */}
              <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-green-500 transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Unread Alerts</p>
                    <p className="text-3xl font-bold text-white">
                      {notifications.filter((n) => !n.read).length}
                    </p>
                  </div>
                  <Bell className="text-green-400" size={32} />
                </div>
              </div>
            </div>

            {/* Top Program */}
            {stats.topProgram && (
              <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mb-8">
                <h2 className="text-xl font-bold text-white mb-4">🏆 Most Reacted Program</h2>
                <div className="flex gap-4">
                  {stats.topProgram.flyerUrl && (
                    <img
                      src={stats.topProgram.flyerUrl}
                      alt={stats.topProgram.title}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {stats.topProgram.title}
                    </h3>
                    <p className="text-slate-400 text-sm mb-3 line-clamp-2">
                      {stats.topProgram.description}
                    </p>
                    <div className="flex gap-4 text-sm">
                      <span className="text-slate-300">
                        Total Reactions: <strong>{stats.topProgram.reactionsCount}</strong>
                      </span>
                      <span className="text-slate-300">
                        Likes: <strong>{stats.topProgram.reactionsData?.like || 0}</strong>
                      </span>
                      <span className="text-slate-300">
                        Loves: <strong>{stats.topProgram.reactionsData?.love || 0}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Programs List */}
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-700">
                <h2 className="text-xl font-bold text-white">All Programs</h2>
              </div>

              {programs.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-slate-400">No programs yet. Create your first one!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-700 border-b border-slate-600">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-300">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-300">
                          Reactions
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-300">
                          👍 Like
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-300">
                          ❤️ Love
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-300">
                          😲 Wow
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-300">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {programs.map((program) => (
                        <tr
                          key={program.id}
                          className="hover:bg-slate-700 transition"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {program.flyerUrl && (
                                <img
                                  src={program.flyerUrl}
                                  alt={program.title}
                                  className="w-10 h-10 object-cover rounded"
                                />
                              )}
                              <div>
                                <p className="text-white font-medium">{program.title}</p>
                                <p className="text-xs text-slate-400 line-clamp-1">
                                  {program.description}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-white font-semibold">
                            {program.reactionsCount || 0}
                          </td>
                          <td className="px-6 py-4 text-slate-300">
                            {program.reactionsData?.like || 0}
                          </td>
                          <td className="px-6 py-4 text-slate-300">
                            {program.reactionsData?.love || 0}
                          </td>
                          <td className="px-6 py-4 text-slate-300">
                            {program.reactionsData?.wow || 0}
                          </td>
                          <td className="px-6 py-4 text-slate-400 text-sm">
                            {program.createdAt?.toDate?.()?.toLocaleDateString() ||
                              new Date().toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <ProgramUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={fetchDashboardData}
      />
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        onSuccess={() => {
          alert('Alert sent successfully!');
          fetchDashboardData();
        }}
      />
    </div>
  );
};

export default AdminProgramsDashboard;
